import { createElement } from 'react'
import type { ReactElement } from 'react'
import type { DocumentProps } from '@react-pdf/renderer'
import type { ReportBrand, ReportDocumentProps } from '../components/ReportDocument'

export interface RenderPdfOptions {
  /** Registers the brand's typefaces before rendering; null keeps Helvetica */
  brand: ReportBrand | null | undefined
}

/**
 * Renders any react-pdf <Document> element to a Blob (spec 008, R8). Shared
 * by reports (below), quotes and the branding sample.
 * react-pdf is imported dynamically so it stays out of the initial bundle;
 * branded renders embed the preset's typefaces, unbranded renders register
 * nothing and keep the base-14 output.
 */
export async function renderPdfBlob(element: ReactElement, opts: RenderPdfOptions): Promise<Blob> {
  const [{ pdf }, { registerBrandFonts }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('./pdfFontService'),
  ])
  await registerBrandFonts(opts.brand)
  // Components render react-pdf's <Document> but pdf() types expect a
  // ReactElement<DocumentProps>; the cast bridges that gap.
  return pdf(element as unknown as ReactElement<DocumentProps>).toBlob()
}

/**
 * Renders assembled report data to a PDF blob. Shared by the generation
 * pipeline (reportsSlice) and the on-screen PDF preview (ReportPreview),
 * so the previewed document is byte-identical to the final one.
 *
 * One pass. The section anchors record their page into `sectionPages` as
 * react-pdf paginates, and the TOC reads it through render props, which
 * react-pdf evaluates again for every page once pagination is complete.
 * This used to be two full renders (one only to learn the page numbers);
 * on a 288-tree report the first pass alone took WebKit's physical
 * footprint to ~3.9 GB and iOS Safari killed the tab.
 */
export async function renderReportPdfBlob(reportData: ReportDocumentProps): Promise<Blob> {
  const { ReportDocument } = await import('../components/ReportDocument')
  const sectionPages: Record<string, number> = {}
  return renderPdfBlob(
    createElement(ReportDocument, {
      ...reportData,
      tocPageNumbers: sectionPages,
      onSectionPage: (id: string, page: number) => {
        sectionPages[id] = page
      },
    }),
    { brand: reportData.brand }
  )
}
